import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Coins, UserPlus, ShoppingBag, Users, Gift, Check, 
  Sparkles, Shield, Clock, Crown, Award, Diamond, MessageCircle, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

export default function HowItWorks() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const steps = [
    {
      icon: UserPlus,
      title: t('howItWorks.steps.step1.title', 'Create Account'),
      desc: t('howItWorks.steps.step1.desc', 'Sign up free and get 5 welcome tokens'),
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Coins,
      title: t('howItWorks.steps.step2.title', 'Buy Tokens'),
      desc: t('howItWorks.steps.step2.desc', 'Purchase Ella Gold Tokens starting from €50'),
      color: 'from-[#D4AF37] to-[#8B7025]'
    },
    {
      icon: ShoppingBag,
      title: t('howItWorks.steps.step3.title', 'Browse & Shop'),
      desc: t('howItWorks.steps.step3.desc', 'Use your tokens to buy exclusive digital art'),
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Gift,
      title: t('howItWorks.steps.step4.title', 'Earn More'),
      desc: t('howItWorks.steps.step4.desc', 'Invite friends and earn bonus tokens'),
      color: 'from-green-500 to-green-600'
    }
  ];

  const loyaltyLevels = [
    {
      tier: t('rewards.bronze.level', 'Bronze'),
      icon: Award,
      color: '#CD7F32',
      minSpent: '€0',
      perks: [t('rewards.bronze.perks', 'Basic Access')],
      special: null
    },
    {
      tier: t('rewards.silver.level', 'Silver'),
      icon: Shield,
      color: '#C0C0C0',
      minSpent: '€15+',
      perks: [t('rewards.silver.perks', '5% Discount')],
      special: null
    },
    {
      tier: t('rewards.gold.level', 'Gold'),
      icon: Crown,
      color: '#D4AF37',
      minSpent: '€30+',
      perks: [t('rewards.gold.perks', '10% + Discord'), t('goldChat.vipChat', 'VIP Chat')],
      special: '🔥 ' + t('goldChat.vipChat', 'VIP Chat') + '!'
    },
    {
      tier: t('rewards.diamond.level', 'Diamond'),
      icon: Diamond,
      color: '#B9F2FF',
      minSpent: '€100+',
      perks: [t('rewards.diamond.perks', 'Secret Vault'), 'Discord', 'VIP'],
      special: '💎 ALL UNLOCKED!'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm mb-6">
            <Sparkles className="w-4 h-4" />
            {t('howItWorks.badge', 'Getting Started')}
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {t('howItWorks.title', 'How It')} <span className="text-[#D4AF37]">{t('howItWorks.titleAccent', 'Works')}</span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t('howItWorks.subtitle', 'Everything you need to know about Ella Gold')}
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="relative">
                  <div className="text-center p-4 md:p-6 glass rounded-xl border border-white/10 hover:border-[#D4AF37]/50 transition-all">
                    <div className={`w-16 h-16 mx-auto rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-3 shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{step.title}</h3>
                    <p className="text-gray-400 text-sm">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOYALTY LEVELS */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#D4AF37]/5 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <Crown className="w-10 h-10 text-[#D4AF37] inline mr-3" />
              {t('rewards.title', 'Collector')} <span className="text-[#D4AF37]">{t('rewards.titleAccent', 'Rewards')}</span>
            </h2>
            <p className="text-gray-400 text-lg">
              {t('rewards.subtitle', 'Earn badges and unlock exclusive perks as you collect more art.')}
            </p>
          </div>

          {/* Loyalty Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loyaltyLevels.map((level, idx) => {
              const Icon = level.icon;
              const isGold = level.tier.toLowerCase().includes('gold') || level.tier.toLowerCase().includes('goud') || level.tier.toLowerCase().includes('or');
              return (
                <div 
                  key={idx} 
                  className={`p-6 rounded-xl border ${isGold ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/10 bg-white/5'}`}
                >
                  <div className="text-center mb-4">
                    <Icon 
                      className="w-12 h-12 mx-auto mb-2" 
                      style={{ color: level.color, filter: `drop-shadow(0 0 10px ${level.color}50)` }} 
                    />
                    <h3 className="text-xl font-bold" style={{ color: level.color }}>{level.tier}</h3>
                    <p className="text-2xl font-bold text-white mt-1">{level.minSpent}</p>
                  </div>
                  
                  {level.special && (
                    <div className="mb-4 p-2 rounded-lg bg-[#D4AF37]/20 text-center">
                      <span className="text-sm font-bold text-[#D4AF37]">{level.special}</span>
                    </div>
                  )}
                  
                  <ul className="space-y-2">
                    {level.perks.map((perk, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{perk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Special Gold Callout */}
          <div className="mt-8 p-6 glass rounded-2xl border border-[#D4AF37]/50 bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
            <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div className="flex-shrink-0">
                <MessageCircle className="w-16 h-16 text-[#D4AF37]" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-[#D4AF37] mb-2">💬 {t('goldChat.vipChat', 'VIP Chat')}</h3>
                <p className="text-gray-300">
                  {t('goldChat.upgradeMessage', 'Upgrade to Gold or Diamond membership to chat privately with Ella.')}
                </p>
              </div>
              <Button 
                onClick={() => navigate('/credits')}
                className="bg-[#D4AF37] hover:bg-[#B8963A] text-black px-6 py-3 flex-shrink-0"
              >
                {t('goldChat.upgradeNow', 'Upgrade to Gold')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Token Prices */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            <Coins className="w-8 h-8 text-[#D4AF37] inline mr-2" />
            {t('tokens.title', 'Buy')} {t('tokens.titleAccent', 'Tokens')}
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-6 glass rounded-xl text-center border border-white/10">
              <div className="text-4xl font-bold text-[#D4AF37] mb-1">50</div>
              <div className="text-gray-400 mb-3">{t('tokens.tokenUnit', 'tokens')}</div>
              <div className="text-2xl font-bold">€50</div>
              <div className="text-xs text-gray-500 mt-1">€1 / token</div>
            </div>
            
            <div className="p-6 glass rounded-xl text-center border border-[#D4AF37]/50 bg-[#D4AF37]/5 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full">
                {t('tokens.popular', 'POPULAR')}
              </div>
              <div className="text-4xl font-bold text-[#D4AF37] mb-1">110</div>
              <div className="text-gray-400 mb-3">{t('tokens.tokenUnit', 'tokens')} (+10 {t('tokens.bonus', 'bonus')}!)</div>
              <div className="text-2xl font-bold">€100</div>
              <div className="text-xs text-green-400 mt-1">10% {t('tokens.bonus', 'bonus')}!</div>
            </div>
            
            <div className="p-6 glass rounded-xl text-center border border-white/10">
              <div className="text-4xl font-bold text-[#D4AF37] mb-1">230</div>
              <div className="text-gray-400 mb-3">{t('tokens.tokenUnit', 'tokens')} (+30 {t('tokens.bonus', 'bonus')}!)</div>
              <div className="text-2xl font-bold">€180</div>
              <div className="text-xs text-green-400 mt-1">15% {t('tokens.bonus', 'bonus')}!</div>
            </div>
          </div>

          <div className="text-center mt-6">
            <Button 
              onClick={() => navigate('/credits')}
              className="bg-[#D4AF37] hover:bg-[#B8963A] text-black px-8 py-6 text-lg"
            >
              <Coins className="w-5 h-5 mr-2" />
              {t('tokens.buyTokens', 'Buy Tokens')}
            </Button>
          </div>
        </div>
      </section>

      {/* Referral */}
      <section className="py-16 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 glass rounded-2xl border border-green-500/30 bg-gradient-to-r from-green-500/10 to-transparent">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <Users className="w-12 h-12 text-green-400" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold mb-2">🎁 {t('howItWorks.referral.title', 'Referral Program')}</h3>
                <p className="text-gray-400 mb-2">
                  {t('howItWorks.referral.desc', 'Earn 10 free tokens for every friend you refer!')}
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-green-400 mb-1">+10</div>
                <div className="text-sm text-gray-400">{t('howItWorks.referral.perReferral', 'tokens per referral')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick FAQ */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">{t('howItWorks.faq.title', 'FAQ')}</h2>
          
          <div className="space-y-4">
            <div className="p-5 glass rounded-xl">
              <h3 className="font-semibold text-[#D4AF37] mb-2">{t('howItWorks.faq.q1', 'What are Ella Gold Tokens?')}</h3>
              <p className="text-gray-400">{t('howItWorks.faq.a1', 'Ella Gold Tokens are our digital currency that you use to purchase content in our shop.')}</p>
            </div>
            <div className="p-5 glass rounded-xl">
              <h3 className="font-semibold text-[#D4AF37] mb-2">{t('howItWorks.faq.q3', 'Do tokens expire?')}</h3>
              <p className="text-gray-400">{t('howItWorks.faq.a3', 'No! Your tokens never expire and stay in your account forever.')}</p>
            </div>
            <div className="p-5 glass rounded-xl">
              <h3 className="font-semibold text-[#D4AF37] mb-2">{t('howItWorks.faq.q2', 'How do I get tokens?')}</h3>
              <p className="text-gray-400">{t('howItWorks.faq.a2', 'You can buy tokens directly on our website, or earn them by referring friends.')}</p>
            </div>
            <div className="p-5 glass rounded-xl">
              <h3 className="font-semibold text-[#D4AF37] mb-2">{t('howItWorks.faq.q4', 'What can I buy with tokens?')}</h3>
              <p className="text-gray-400">{t('howItWorks.faq.a4', 'Everything in our shop: digital art, guides, custom creations, and more.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t border-white/10">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">{t('howItWorks.cta.title', 'Ready to start?')}</h2>
          <p className="text-gray-400 mb-8">{t('howItWorks.cta.subtitle', 'Create an account and receive 5 free welcome tokens!')}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/register')}
              className="bg-[#D4AF37] hover:bg-[#B8963A] text-black px-8 py-6 text-lg"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              {t('howItWorks.cta.register', 'Create Free Account')}
            </Button>
            <Button
              onClick={() => navigate('/shop')}
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-6 text-lg"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              {t('howItWorks.cta.viewPackages', 'View Token Packages')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
